import { memo } from 'react';
import { useHistory } from 'react-router-dom';
import { useNavigation } from 'hooks';
import { EventModel } from 'models';

import { ShowUnread, QueryKey } from 'pages/events-list/constants';

import {
  WidgetContainer,
  MessagesIcon,
  IconButton,
  EmptyMessage,
} from 'components';
import MessageWidgetItem from './message-widget-item';
import { Box } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import clsx from 'clsx';
import { useStyles } from './style';

interface Props {
  messages: EventModel[];
}

const MessageWidget = ({ messages }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    data: slicedMessages,
    disableNextButton,
    showNextPage,
    disabledPrevButton,
    showPrevPage,
    totalPages,
  } = useNavigation(messages, { itemsPerPage: 3 });

  const handleClickMessageIcon = () => {
    const showUnreadValue = messages.length ? ShowUnread.Yes : ShowUnread.No;

    history.push(`/event-list?${QueryKey.ShowUnread}=${showUnreadValue}`);
  };

  const handleClickWidgetRibbon = () => {
    history.push('/event-list');
  };

  const redirectToEventMessage = (id: string) => {
    history.push(`/event-list/${id}`);
  };

  const renderEmptyWidgetContent = () => {
    return (
      <Box
        display="flex"
        alignItems="center"
        padding={{ xs: '15px', sm: '20px' }}
        paddingTop={0}
        height="100%"
      >
        <EmptyMessage className={classes.emptyMessageIcon} />
        <Box padding={{ xs: '0 10px', sm: '0 30px' }}>
          <span className={clsx(classes.emptyText, classes.bold)}>
            Tässä näet uusimmat viestit ja ilmoitukset taloyhtiöltä ja
            taloyhtiön hallitukselta.
          </span>
          <span className={clsx(classes.emptyText, classes.greyish)}>
            Ei uusia viestejä.
          </span>
        </Box>
      </Box>
    );
  };

  const renderMessages = () => {
    return (
      <Box
        display="flex"
        alignItems="center"
        padding={{ xs: '0 2px 30px', sm: '0 10px 30px' }}
        height="100%"
      >
        {totalPages > 1 ? (
          <IconButton
            className={classes.iconBtn}
            onClick={showPrevPage}
            disabled={disabledPrevButton}
            data-testid="arrow-left"
          >
            <ChevronLeftIcon className={classes.arrowIcon} />
          </IconButton>
        ) : null}
        <Box width="100%" margin="0 5px" data-testid="items-wrapper">
          {slicedMessages.map((message) => (
            <MessageWidgetItem
              key={message.Id}
              message={message}
              onClick={redirectToEventMessage}
            />
          ))}
        </Box>
        {totalPages > 1 ? (
          <IconButton
            className={classes.iconBtn}
            onClick={showNextPage}
            disabled={disableNextButton}
            data-testid="arrow-right"
          >
            <ChevronRightIcon className={classes.arrowIcon} />
          </IconButton>
        ) : null}
      </Box>
    );
  };

  const showMessages = !!messages.length;

  return (
    <WidgetContainer
      themeColor="darkBlue"
      title={'Kaikki viestit'}
      action={handleClickWidgetRibbon}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="15px"
      >
        <h3 className={classes.title}>Viestit</h3>
        {showMessages ? (
          <MessagesIcon
            style={{ cursor: 'pointer' }}
            count={messages.length}
            onClick={handleClickMessageIcon}
          />
        ) : null}
      </Box>
      {showMessages ? renderMessages() : renderEmptyWidgetContent()}
    </WidgetContainer>
  );
};

export default memo(MessageWidget);
